import { useMemo, useState } from 'react';
import { CopyButton } from '@Components/CopyButton';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { Appearance, Size } from '@Components/designConstants';
import { formatPercent } from '../data-qa-utils';
import {
  buildGroundTruthSql,
  computeAggregateDeviation,
  GroundTruthSqlWeek
} from '../ground-truth-sql';

interface GroundTruthSqlPanelProps {
  company: string;
  weeks: GroundTruthSqlWeek[];
  channelLabel?: string;
  // dataset.reviewGranularity -- see buildGroundTruthSql's dateTruncUnitFor doc
  // comment for why the canonical-side SQL must bucket by the same grain as `weeks`.
  reviewGranularity?: string;
  // Forwarded straight to buildGroundTruthSql -- see its own doc comment. Only passed
  // by ChannelReviewSection when the channel has more than one platform (matching the
  // chart's own per-platform breakout).
  platforms?: { key: string; label: string }[];
  // Forwarded straight to buildGroundTruthSql -- see its own doc comment. Only passed
  // by ChannelReviewSection for a channel ChannelConfig.group split (channel.label !==
  // channel.rawLabel).
  featureIds?: string[];
}

// Literal DataGrip ${paramName} placeholder text shown to the reviewer, not a JS
// template literal.
// eslint-disable-next-line no-template-curly-in-string
const THRESHOLD_PLACEHOLDER = '${threshold_pct}';

// Reuses formatPercent for the digits+% (rounding, decimal formatting), just adding
// the leading '+' formatPercent itself doesn't -- Justin's ask (2026-08-31): use the
// shared formatter where it fits rather than hand-rolling the whole thing.
const signedPct = (fraction: number) => `${fraction > 0 ? '+' : ''}${formatPercent(fraction, 1)}`;

// The compare-to-canonical (BPM) layer folded into a review section: an always-visible
// deviation readout (no fixed pass/fail cutoff -- just the actual MMM/BPM - 1 numbers,
// since deciding what deviation warrants a look is a judgment call, not something this
// panel should bake in) plus a collapsible SQL query the reviewer runs themselves,
// parameterized by a threshold they choose, to drill into which specific weeks are
// responsible.
export const GroundTruthSqlPanel = ({
  company,
  weeks,
  channelLabel,
  reviewGranularity,
  platforms,
  featureIds
}: GroundTruthSqlPanelProps) => {
  const [open, setOpen] = useState(false);
  const deviation = useMemo(() => computeAggregateDeviation(weeks), [weeks]);
  const sql = useMemo(
    () =>
      buildGroundTruthSql({
        company,
        weeks,
        channelLabel,
        reviewGranularity,
        platforms,
        featureIds
      }),
    [company, weeks, channelLabel, reviewGranularity, platforms, featureIds]
  );

  return (
    <div className='data-qa-gt'>
      {deviation && (
        <div className='data-qa-gt__deviation'>
          MMM vs. canonical (BPM), training window total: spend {signedPct(deviation.spendPct)} ·
          impressions {signedPct(deviation.impressionsPct)}
        </div>
      )}

      <Button
        appearance={Appearance.EMPTY}
        size={Size.SM}
        className='data-qa-gt__toggle'
        aria-expanded={open}
        leadingElement={open ? <MdExpandLess /> : <MdExpandMore />}
        onClick={() => setOpen(o => !o)}
      >
        Compare to canonical (BPM)
      </Button>

      {open && (
        <div className='data-qa-gt__detail'>
          <div className='data-qa-gt__sql'>
            <div className='data-qa-gt__sql-header'>
              <div className='data-qa-gt__sql-label'>
                Run in Redshift to pull weeks whose spend or impressions deviate from the MMM
                dataframe beyond a threshold you choose -- fill in{' '}
                <code>{THRESHOLD_PLACEHOLDER}</code> (e.g. 1 for 1%) before running.
              </div>
              <CopyButton text={sql} aria-label='Copy SQL to clipboard' tooltipLabel='Copy SQL' />
            </div>
            <pre>{sql}</pre>
          </div>
        </div>
      )}
    </div>
  );
};
